/**
 * Pluralsight-style hero — dark gradient B2B skills-platform opener
 * with a CTA pair, a proof line, and a 4-up initiative-card grid (the
 * first card carries the brand accent gradient).
 *
 * <p>Companion to the {@code PluralsightHeroView} admin canvas preview
 * in {@code admin/src/components/app/blockViews.tsx} — that renders a
 * static mock for the editor; this is the real, responsive block.
 */
import { useMemo } from 'react';
import { Cloud, Bot, Shield, Code2, ArrowRight, Sparkles, type LucideIcon } from 'lucide-react';
import { tx, asList } from '../_shared/coerce';
import { kickerStyle } from '../_shared/styles';
import { Button } from '@akademi/ui';
import {
  PortalLink,
  isVisible,
  useCurrentUser,
  type CurrentUser,
} from '@akademi/site-builder-runtime';
import type { CampaignAudience } from '../_shared/campaignAudience';
import { HeroEmptyFallback } from '../_shared/HeroEmptyFallback';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface PluralsightHeroCard extends CampaignAudience {
  icon?: string;
  title?: Localized;
  body?: Localized;
  href?: string;
}

export interface PluralsightHeroProps {
  eyebrow?: Localized;
  title?: Localized;
  subtitle?: Localized;
  primaryCtaLabel?: Localized;
  primaryCtaHref?: string;
  secondaryCtaLabel?: Localized;
  secondaryCtaHref?: string;
  proofValue?: string;
  proofLabel?: Localized;
  cards?: PluralsightHeroCard[];
}

const ICON_FOR: Record<string, LucideIcon> = {
  cloud: Cloud,
  robot: Bot,
  shield: Shield,
  code: Code2,
};

export function PluralsightHero({
  eyebrow,
  title,
  subtitle,
  primaryCtaLabel,
  primaryCtaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
  proofValue,
  proofLabel,
  cards,
}: PluralsightHeroProps) {
  const currentUser = useCurrentUser<CurrentUser>();
  const list = useMemo(
    () => asList<PluralsightHeroCard>(cards).filter((c) => isVisible(c, currentUser)),
    [cards, currentUser],
  );

  return (
    <BlockRoot
      as="section"
      style={{
        padding: 'clamp(40px, 6vw, 72px) clamp(20px, 5vw, 64px)',
        background:
          'linear-gradient(135deg, var(--color-neutral-900, #0b0b10), var(--color-brand-900, #24132d))',
        color: '#fff',
      }}
    >
      <div
        style={{
          maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
          width: '100%',
          margin: '0 auto',
        }}
      >
        {eyebrow && (
          <div
            style={{
              ...kickerStyle,
              color: 'var(--color-brand-400, #ffb84d)',
              fontWeight: 800,
            }}
          >
            {tx(eyebrow)}
          </div>
        )}
        <h1
          style={{
            margin: '12px 0 0',
            fontFamily: 'var(--font-family-display, inherit)',
            fontSize: 'clamp(32px, 5vw, 52px)',
            lineHeight: 0.98,
            fontWeight: 900,
            letterSpacing: '-0.03em',
            maxWidth: 720,
          }}
        >
          {tx(title) || 'Deploy tech initiatives faster'}
        </h1>
        {subtitle && (
          <p
            style={{
              margin: '16px 0 0',
              fontSize: 'clamp(15px, 1.6vw, 18px)',
              lineHeight: 1.55,
              color: 'rgba(255,255,255,0.72)',
              maxWidth: 580,
            }}
          >
            {tx(subtitle)}
          </p>
        )}

        {(primaryCtaLabel || secondaryCtaLabel) && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--space-3, 12px)',
              marginTop: 'var(--space-8, 32px)',
            }}
          >
            {primaryCtaLabel && (
              <Button
                href={primaryCtaHref ?? '#'}
                size="lg"
                style={{
                  height: 'auto',
                  gap: 'var(--space-2, 8px)',
                  padding: 'var(--space-3, 12px) var(--space-6, 24px)',
                  fontWeight: 700,
                  background: 'var(--color-brand-500, #ec008c)',
                }}
              >
                {tx(primaryCtaLabel)}
                <ArrowRight aria-hidden size={18} />
              </Button>
            )}
            {secondaryCtaLabel && (
              <Button
                href={secondaryCtaHref ?? '#'}
                variant="outline"
                size="lg"
                style={{
                  height: 'auto',
                  padding: 'var(--space-3, 12px) var(--space-6, 24px)',
                  fontWeight: 700,
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.35)',
                }}
              >
                {tx(secondaryCtaLabel)}
              </Button>
            )}
          </div>
        )}

        {proofValue && (
          <div
            style={{
              marginTop: 'var(--space-6, 24px)',
              display: 'flex',
              alignItems: 'baseline',
              gap: 'var(--space-2, 8px)',
              fontSize: 'var(--font-size-sm, 14px)',
              color: 'rgba(255,255,255,0.6)',
            }}
          >
            <span style={{ fontSize: 'var(--font-size-xl, 22px)', fontWeight: 800, color: '#fff' }}>
              {proofValue}
            </span>
            {proofLabel && <span>{tx(proofLabel)}</span>}
          </div>
        )}

        {list.length === 0 && (
          <HeroEmptyFallback
            style={{
              marginTop: 'clamp(28px, 4vw, 40px)',
              minHeight: 108,
              borderRadius: 'var(--radius-lg, 12px)',
            }}
          />
        )}

        {list.length > 0 && (
          <div
            className="ps-hero-cards"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              gap: 'var(--space-3, 12px)',
              marginTop: 'clamp(28px, 4vw, 40px)',
            }}
          >
            {list.slice(0, 4).map((it, i) => {
              const Icon = (it.icon && ICON_FOR[it.icon.toLowerCase()]) || Sparkles;
              return (
                <PortalLink
                  key={i}
                  href={it.href ?? '#'}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-2, 8px)',
                    minHeight: 108,
                    borderRadius: 'var(--radius-lg, 12px)',
                    padding: 'var(--space-3-5, 14px)',
                    textDecoration: 'none',
                    color: 'inherit',
                    background:
                      i === 0
                        ? 'linear-gradient(135deg, var(--color-brand-500, #ec008c), var(--color-brand-600, #ff6b00))'
                        : 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.14)',
                  }}
                >
                  <Icon aria-hidden size={18} style={{ opacity: 0.9 }} />
                  <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm, 13px)' }}>
                    {tx(it.title) || `Initiative ${i + 1}`}
                  </div>
                  {it.body && (
                    <div style={{ fontSize: 'var(--font-size-xs, 12px)', opacity: 0.78 }}>
                      {tx(it.body)}
                    </div>
                  )}
                </PortalLink>
              );
            })}
          </div>
        )}
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (max-width: 860px) {
          .ps-hero-cards { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 480px) {
          .ps-hero-cards { grid-template-columns: 1fr !important; }
        }
      `,
        }}
      />
    </BlockRoot>
  );
}
